import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import QRCodeStyling from "qr-code-styling";
import SolidColorBox from "./components/SolidColorBox";
import GradientColorBox from "./components/GradientColorBox";
import NoDataPlaceholder from "./components/common/NoDataPlaceholder";
import ErrorPlaceholder from "./components/common/ErrorPlaceholder";

const qrCode = new QRCodeStyling({});

// Custom hook for color management
const useColorState = (
  initialSolidColor: string,
  initialGradientStart: string,
  initialGradientEnd: string
) => {
  const [radioColor, setRadioColor] = useState("single");
  const [solidColor, setSolidColor] = useState(initialSolidColor);
  const [gradientColor, setGradientColor] = useState<any>(null);
  const [gradientColorType, setGradientColorType] = useState("linear");
  const [gradientColorRotation, setGradientColorRotation] = useState(0);
  const [gradientColorStart, setGradientColorStart] =
    useState(initialGradientStart);
  const [gradientColorEnd, setGradientColorEnd] = useState(initialGradientEnd);

  const updateGradient = useCallback(() => {
    const dataGradient = {
      type: gradientColorType,
      rotation: gradientColorRotation,
      colorStops: [
        { offset: 0, color: gradientColorStart },
        { offset: 1, color: gradientColorEnd },
      ],
    };
    setGradientColor(radioColor === "single" ? null : dataGradient);
  }, [
    gradientColorType,
    gradientColorRotation,
    gradientColorStart,
    gradientColorEnd,
    radioColor,
  ]);

  useEffect(() => {
    updateGradient();
  }, [updateGradient]);

  const handleRadioChange = useCallback(
    (value: string) => {
      setRadioColor(value);
      if (value === "gradient") {
        updateGradient();
      }
    },
    [updateGradient]
  );

  return {
    radioColor,
    solidColor,
    gradientColor,
    gradientColorType,
    gradientColorRotation,
    gradientColorStart,
    gradientColorEnd,
    setRadioColor: handleRadioChange,
    setSolidColor,
    setGradientColorType,
    setGradientColorRotation,
    setGradientColorStart,
    setGradientColorEnd,
  };
};

function SingleQR() {
  // Main QR settings
  const [qrSettings, setQrSettings] = useState({
    dataUrl: "https://phong.info",
    fileExt: "png",
    imageFile: "",
    width: 1500,
    height: 1500,
    margin: 70,
    disableDownload: false,
  });

  // Style settings
  const [styleSettings, setStyleSettings] = useState({
    dotStyle: "square",
    cornerSquareStyle: "square",
    cornerDotStyle: "square",
  });

  // QR options
  const [qrOptions, setQrOptions] = useState({
    typeNumber: 0,
    mode: "Byte",
    correctLevel: "Q",
  });

  const [qrError, setQrError] = useState<string | null>(null);

  // Image options
  const [imageOptions, setImageOptions] = useState({
    hideBGDot: true,
    margin: 20,
    size: 0.4,
  });

  // Color states using custom hook
  const dotColors = useColorState("#000000", "#5614b0", "#fbc82e");
  const cornerSquareColors = useColorState("#000000", "#5614b0", "#fbc82e");
  const cornerDotColors = useColorState("#000000", "#5614b0", "#fbc82e");
  const backgroundColors = useColorState("#ffffff", "#5614b0", "#fbc82e");

  const qrRef = useRef<HTMLDivElement>(null);
  const imageFileRef = useRef<HTMLInputElement>(null);

  // Memoized QR configuration (dependencies narrowed to primitives)
  const qrConfig = useMemo(
    () => ({
      data: qrSettings.dataUrl,
      image: qrSettings.imageFile,
      width: qrSettings.width,
      height: qrSettings.height,
      margin: qrSettings.margin,
      dotsOptions: {
        type: styleSettings.dotStyle as any,
        color: dotColors.solidColor,
        gradient: dotColors.gradientColor,
      },
      cornersSquareOptions: {
        type: styleSettings.cornerSquareStyle as any,
        color: cornerSquareColors.solidColor,
        gradient: cornerSquareColors.gradientColor,
      },
      cornersDotOptions: {
        type: styleSettings.cornerDotStyle as any,
        color: cornerDotColors.solidColor,
        gradient: cornerDotColors.gradientColor,
      },
      backgroundOptions: {
        color: backgroundColors.solidColor,
        gradient: backgroundColors.gradientColor,
      },
      imageOptions: {
        crossOrigin: "anonymous",
        imageHideBGDots: imageOptions.hideBGDot,
        margin: imageOptions.margin,
        imageSize: imageOptions.size,
      },
      qrOptions: {
        typeNumber: qrOptions.typeNumber as any,
        mode: qrOptions.mode as any,
        errorCorrectionLevel: qrOptions.correctLevel as any,
      },
    }),
    [
      qrSettings.dataUrl,
      qrSettings.imageFile,
      qrSettings.width,
      qrSettings.height,
      qrSettings.margin,
      styleSettings.dotStyle,
      styleSettings.cornerSquareStyle,
      styleSettings.cornerDotStyle,
      dotColors.solidColor,
      dotColors.gradientColor,
      cornerSquareColors.solidColor,
      cornerSquareColors.gradientColor,
      cornerDotColors.solidColor,
      cornerDotColors.gradientColor,
      backgroundColors.solidColor,
      backgroundColors.gradientColor,
      imageOptions.hideBGDot,
      imageOptions.margin,
      imageOptions.size,
      qrOptions.typeNumber,
      qrOptions.mode,
      qrOptions.correctLevel,
    ]
  );

  useEffect(() => {
    const qrElement = qrRef.current;

    // Create QR instance safely
    if (qrElement) {
      try {
        qrCode.append(qrElement);
      } catch (error) {
        console.error("Error appending QR code:", error);
      }
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "Are you sure you want to leave?";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // Cleanup QR instance safely
      try {
        if (qrElement) {
          qrElement.innerHTML = "";
        }
      } catch (error) {
        console.error("Error cleaning up QR code:", error);
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const noData = !qrSettings.dataUrl || !String(qrSettings.dataUrl).trim();
    try {
      if (noData) {
        // Show "No QR Data" placeholder when no data (handled in JSX now)
        if (qrRef.current) {
          qrRef.current.innerHTML = "";
        }
        setQrError(null);
        setQrSettings((prev) =>
          prev.disableDownload ? prev : { ...prev, disableDownload: true }
        );
        return;
      }

      // Ensure QR is mounted if placeholder cleared previously
      if (
        qrRef.current &&
        (!qrRef.current.firstChild ||
          qrRef.current.innerHTML.indexOf("No QR data") !== -1)
      ) {
        qrRef.current.innerHTML = "";
        try {
          qrCode.append(qrRef.current);
        } catch (appendError) {
          console.error("Error appending QR code:", appendError);
          return;
        }
      }

      setQrError(null);
      qrCode.update(qrConfig);
      setQrSettings((prev) =>
        prev.disableDownload ? { ...prev, disableDownload: false } : prev
      );
    } catch (error) {
      console.error("An error occurred:", error);
      
      const errorMsg = String(error).includes("code length overflow") 
        ? "Try adjusting the Type Number in QR Options"
        : "Cannot encode this data. Please try again.";
      setQrError(errorMsg);

      // Clear container on error to prevent DOM issues
      if (qrRef.current) {
        try {
          qrRef.current.innerHTML = "";
        } catch (domError) {
          console.error("Error updating DOM:", domError);
        }
      }
      setQrSettings((prev) =>
        prev.disableDownload ? prev : { ...prev, disableDownload: true }
      );
    }
  }, [qrConfig, qrSettings.dataUrl, qrSettings.width]);

  // Optimized event handlers with useCallback
  const updateQrSettings = useCallback((updates: any) => {
    setQrSettings((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateStyleSettings = useCallback((updates: any) => {
    setStyleSettings((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateQrOptions = useCallback((updates: any) => {
    setQrOptions((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateImageOptions = useCallback((updates: any) => {
    setImageOptions((prev) => ({ ...prev, ...updates }));
  }, []);

  // Optimized event handlers
  const onDataUrlChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      updateQrSettings({ dataUrl: event.target.value });
    },
    [updateQrSettings]
  );

  const onDataUrlFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    // Select toàn bộ nội dung khi focus
    event.target.select();
  }, []);

  const onChangeImageFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (window.FileReader) {
        const reader = new FileReader();
        reader.onload = (e) => {
          updateQrSettings({ imageFile: e.target?.result as string });
        };
        reader.readAsDataURL(file);
      }
    },
    [updateQrSettings]
  );

  const onCancelImageFile = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      updateQrSettings({ imageFile: "" });
      if (imageFileRef.current) {
        imageFileRef.current.value = "";
      }
      // Auto focus vào input file sau khi clear
      setTimeout(() => {
        const inputElement = document.getElementById("inputFile");
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    },
    [updateQrSettings]
  );

  const onCancelDataUrl = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      updateQrSettings({ dataUrl: "" });
      // Auto focus vào input sau khi clear
      setTimeout(() => {
        const inputElement = document.getElementById("inputData");
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    },
    [updateQrSettings]
  );

  const onChangeQrWidth = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      updateQrSettings({ width: parseInt(event.target.value) });
    },
    [updateQrSettings]
  );

  const onChangeQrHeight = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      updateQrSettings({ height: parseInt(event.target.value) });
    },
    [updateQrSettings]
  );

  const onChangeQrMargin = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      updateQrSettings({ margin: parseInt(event.target.value) });
    },
    [updateQrSettings]
  );

  // Style handlers
  const onChangeQrDotStyle = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      event.preventDefault();
      updateStyleSettings({ dotStyle: event.target.value });
    },
    [updateStyleSettings]
  );

  const onChangeQrCSStyle = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      event.preventDefault();
      updateStyleSettings({ cornerSquareStyle: event.target.value });
    },
    [updateStyleSettings]
  );

  const onChangeQrCDStyle = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      event.preventDefault();
      updateStyleSettings({ cornerDotStyle: event.target.value });
    },
    [updateStyleSettings]
  );

  // Image and QR options handlers
  const onChangeHideBGDot = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateImageOptions({ hideBGDot: !imageOptions.hideBGDot });
    },
    [updateImageOptions, imageOptions.hideBGDot]
  );

  const onImageSizeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      updateImageOptions({ size: parseFloat(event.target.value) });
    },
    [updateImageOptions]
  );

  const onImageMarginChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      updateImageOptions({ margin: parseInt(event.target.value) });
    },
    [updateImageOptions]
  );

  const onQrTypeNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      updateQrOptions({ typeNumber: parseInt(event.target.value) });
    },
    [updateQrOptions]
  );

  const onQrModeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      event.preventDefault();
      updateQrOptions({ mode: event.target.value });
    },
    [updateQrOptions]
  );

  const onQrCorrectLvChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      event.preventDefault();
      updateQrOptions({ correctLevel: event.target.value });
    },
    [updateQrOptions]
  );

  const onExtensionChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      updateQrSettings({ fileExt: event.target.value });
    },
    [updateQrSettings]
  );

  const getCurrentDateTimeString = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}${month}${day}_${hours}${minutes}${seconds}`;
  }, []);

  const onDownloadQr = useCallback(() => {
    if (qrSettings.disableDownload) return;

    qrCode.download({
      name: `qr_${getCurrentDateTimeString()}`,
      extension: qrSettings.fileExt as any,
    });
  }, [
    qrSettings.disableDownload,
    qrSettings.fileExt,
    getCurrentDateTimeString,
  ]);

  return (
    <>
      <div className="row">
        <div className="col-12 col-md-8">
          <div className="accordion" id="mainAcc">
            {/* accordion-item */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="panelOpenHOne">
                <button
                  className="accordion-button hide-icon"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target=""
                  aria-expanded="true"
                  aria-controls=""
                >
                  QR Main options
                </button>
              </h2>
              <div
                id="panelOCOne"
                className="accordion-collapse collapse show"
                aria-labelledby="panelOpenHOne"
              >
                <div className="accordion-body">
                  <div className="row g-3 mb-4 align-items-center">
                    <div className="col-4 col-md-2 text-end">
                      <label htmlFor="inputData" className="col-form-label">
                        QR Data
                      </label>
                    </div>
                    <div className="col-6 col-md-8">
                      <input
                        value={qrSettings.dataUrl}
                        onChange={onDataUrlChange}
                        onFocus={onDataUrlFocus}
                        type="text"
                        id="inputData"
                        className="form-control highlight"
                        placeholder="Your QR data here..."
                      />
                    </div>
                    <div className="col-2">
                      <button
                        type="button"
                        name="clearQr"
                        onClick={onCancelDataUrl}
                        className="btn btn-secondary w-100 btn-cancel"
                      >
                        <span className="text-white d-lg-none">
                          <svg
                            width="25px"
                            height="25px"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16 8L8 16M8 8L16 16"
                              stroke="#ffffff"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                        <span className="d-none d-lg-inline">Clear</span>
                      </button>
                    </div>
                  </div>

                  <div className="row g-3 mb-4 align-items-center">
                    <div className="col-4 col-md-2 text-end">
                      <label htmlFor="inputFile" className="col-form-label">
                        QR Image
                      </label>
                    </div>
                    <div className="col-6 col-md-8">
                      <input
                        ref={imageFileRef}
                        onChange={onChangeImageFile}
                        type="file"
                        id="inputFile"
                        className="form-control"
                        accept="image/*"
                      />
                    </div>
                    <div className="col-2">
                      <button
                        type="button"
                        name="clearImage"
                        onClick={onCancelImageFile}
                        className="btn btn-secondary w-100 btn-cancel"
                      >
                        <span className="text-white d-lg-none">
                          <svg
                            width="25px"
                            height="25px"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16 8L8 16M8 8L16 16"
                              stroke="#ffffff"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                        <span className="d-none d-lg-inline">Clear</span>
                      </button>
                    </div>
                  </div>

                  <div className="row g-3 align-items-center">
                    <div className="col-4 col-md-2 text-end">
                      <label htmlFor="inputWidth" className="col-form-label">
                        Width (px)
                      </label>
                    </div>
                    <div className="col-8 col-md-2">
                      <input
                        value={qrSettings.width}
                        onChange={onChangeQrWidth}
                        id="inputWidth"
                        className="form-control"
                        type="number"
                        min="100"
                        max="10000"
                      ></input>
                    </div>
                    <div className="col-4 col-md-2 text-end">
                      <label htmlFor="inputHeight" className="col-form-label">
                        Height (px)
                      </label>
                    </div>
                    <div className="col-8 col-md-2">
                      <input
                        value={qrSettings.height}
                        onChange={onChangeQrHeight}
                        id="inputHeight"
                        className="form-control"
                        type="number"
                        min="100"
                        max="10000"
                      ></input>
                    </div>
                    <div className="col-4 col-md-2 text-end">
                      <label htmlFor="inputMargin" className="col-form-label">
                        Margin (px)
                      </label>
                    </div>
                    <div className="col-8 col-md-2">
                      <input
                        value={qrSettings.margin}
                        onChange={onChangeQrMargin}
                        id="inputMargin"
                        className="form-control"
                        type="number"
                        min="0"
                        max="500"
                      ></input>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* accordion-item */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="panelOpenHTwo">
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelOCTwo"
                  aria-expanded="false"
                  aria-controls="panelOCTwo"
                >
                  Dots Options
                </button>
              </h2>
              <div
                id="panelOCTwo"
                className="accordion-collapse collapse"
                aria-labelledby="panelOpenHTwo"
              >
                <div className="accordion-body">
                  <div className="row g-3 mb-4 align-items-center">
                    <div className="col-4 col-md-2 text-end">
                      <label htmlFor="inputDotStyle" className="col-form-label">
                        Dots Style
                      </label>
                    </div>
                    <div className="col-8 col-md-4">
                      <select
                        value={styleSettings.dotStyle}
                        onChange={onChangeQrDotStyle}
                        id="inputDotStyle"
                        className="form-select"
                      >
                        <option value="square">Square</option>
                        <option value="dots">Dot</option>
                        <option value="rounded">Rounded</option>
                        <option value="extra-rounded">Extra rounded</option>
                        <option value="classy">Classy</option>
                        <option value="classy-rounded">Classy rounded</option>
                      </select>
                    </div>
                    <div className="col-4 col-md-2 text-end">
                      <label className="col-form-label">Color Type</label>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="form-check">
                        <input
                          type="radio"
                          value="single"
                          onChange={(e) =>
                            dotColors.setRadioColor(e.target.value)
                          }
                          checked={dotColors.radioColor === "single"}
                          className="form-check-input"
                          name="radioDotColorType"
                          id="radiosolidColor"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="radiosolidColor"
                        >
                          Single
                        </label>
                      </div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="form-check">
                        <input
                          type="radio"
                          value="gradient"
                          onChange={(e) =>
                            dotColors.setRadioColor(e.target.value)
                          }
                          checked={dotColors.radioColor === "gradient"}
                          className="form-check-input"
                          name="radioDotColorType"
                          id="radioGradientColor"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="radioGradientColor"
                        >
                          Gradient
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Type Solid color */}
                  {dotColors.radioColor === "single" && (
                    <SolidColorBox
                      elementName={"DotsOptions"}
                      solidColor={dotColors.solidColor}
                      onChangeSolidColor={(e) =>
                        dotColors.setSolidColor(e.target.value)
                      }
                    />
                  )}

                  {/* Type Gradient color */}
                  {dotColors.radioColor === "gradient" && (
                    <GradientColorBox
                      elementName={"DotsOptions"}
                      gradientColorType={dotColors.gradientColorType}
                      onChangeGradientColorType={(e) =>
                        dotColors.setGradientColorType(e.target.value)
                      }
                      gradientColorStart={dotColors.gradientColorStart}
                      onChangeGradientColorStart={(e) =>
                        dotColors.setGradientColorStart(e.target.value)
                      }
                      gradientColorEnd={dotColors.gradientColorEnd}
                      onChangeGradientColorEnd={(e) =>
                        dotColors.setGradientColorEnd(e.target.value)
                      }
                      gradientColorRotation={dotColors.gradientColorRotation}
                      onChangeGradientColorRotation={(e) =>
                        dotColors.setGradientColorRotation(parseInt(e.target.value) || 0)
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            {/* accordion-item */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="panelOpenHThree">
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelOCThree"
                  aria-expanded="false"
                  aria-controls="panelOCThree"
                >
                  Corners Square Options
                </button>
              </h2>
              <div
                id="panelOCThree"
                className="accordion-collapse collapse"
                aria-labelledby="panelOpenHThree"
              >
                <div className="accordion-body">
                  <div className="row g-3 mb-4 align-items-center">
                    <div className="col-5 col-md-2 text-end">
                      <label htmlFor="inputCSType" className="col-form-label">
                        Corner Square
                      </label>
                    </div>
                    <div className="col-7 col-md-4">
                      <select
                        value={styleSettings.cornerSquareStyle}
                        onChange={onChangeQrCSStyle}
                        id="inputCSType"
                        className="form-select"
                      >
                        <option value="square">Square</option>
                        <option value="dot">Dot</option>
                        <option value="extra-rounded">Extra rounded</option>
                      </select>
                    </div>
                    <div className="col-4 col-md-2 text-end">
                      <label
                        htmlFor="inputCSColorType"
                        className="col-form-label"
                      >
                        Color Type
                      </label>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          value="single"
                          onChange={(e) =>
                            cornerSquareColors.setRadioColor(e.target.value)
                          }
                          checked={cornerSquareColors.radioColor === "single"}
                          type="radio"
                          name="radioCSColor"
                          id="radioCSsolidColor"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="radioCSsolidColor"
                        >
                          Single
                        </label>
                      </div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          value="gradient"
                          onChange={(e) =>
                            cornerSquareColors.setRadioColor(e.target.value)
                          }
                          checked={cornerSquareColors.radioColor === "gradient"}
                          type="radio"
                          name="radioCSColor"
                          id="radioCSGradientColor"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="radioCSGradientColor"
                        >
                          Gradient
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Type Solid color */}
                  {cornerSquareColors.radioColor === "single" && (
                    <SolidColorBox
                      elementName={"CornersSquareOptions"}
                      solidColor={cornerSquareColors.solidColor}
                      onChangeSolidColor={(e) =>
                        cornerSquareColors.setSolidColor(e.target.value)
                      }
                    />
                  )}

                  {/* Type Gradient color */}
                  {cornerSquareColors.radioColor === "gradient" && (
                    <GradientColorBox
                      elementName={"CornersSquareOptions"}
                      gradientColorType={cornerSquareColors.gradientColorType}
                      onChangeGradientColorType={(e) =>
                        cornerSquareColors.setGradientColorType(e.target.value)
                      }
                      gradientColorStart={cornerSquareColors.gradientColorStart}
                      onChangeGradientColorStart={(e) =>
                        cornerSquareColors.setGradientColorStart(e.target.value)
                      }
                      gradientColorEnd={cornerSquareColors.gradientColorEnd}
                      onChangeGradientColorEnd={(e) =>
                        cornerSquareColors.setGradientColorEnd(e.target.value)
                      }
                      gradientColorRotation={
                        cornerSquareColors.gradientColorRotation
                      }
                      onChangeGradientColorRotation={(e) =>
                        cornerSquareColors.setGradientColorRotation(
                          parseInt(e.target.value) || 0
                        )
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            {/* accordion-item */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="panelOpenHFour">
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelOCFour"
                  aria-expanded="false"
                  aria-controls="panelOCFour"
                >
                  Corners Dot Options
                </button>
              </h2>
              <div
                id="panelOCFour"
                className="accordion-collapse collapse"
                aria-labelledby="panelOpenHFour"
              >
                <div className="accordion-body">
                  <div className="row g-3 mb-4 align-items-center">
                    <div className="col-6 col-md-2 text-end">
                      <label htmlFor="inputCDType" className="col-form-label">
                        Corner Dots Style
                      </label>
                    </div>
                    <div className="col-6 col-md-4">
                      <select
                        value={styleSettings.cornerDotStyle}
                        onChange={onChangeQrCDStyle}
                        id="inputCDType"
                        className="form-select"
                      >
                        <option value="square">Square</option>
                        <option value="dot">Dot</option>
                      </select>
                    </div>
                    <div className="col-4 col-md-2 text-end">
                      <label
                        htmlFor="inputCDColorType"
                        className="col-form-label"
                      >
                        Color Type
                      </label>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          value="single"
                          onChange={(e) =>
                            cornerDotColors.setRadioColor(e.target.value)
                          }
                          checked={cornerDotColors.radioColor === "single"}
                          type="radio"
                          name="radioCDColor"
                          id="radioCDSolidColor"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="radioCDSolidColor"
                        >
                          Single
                        </label>
                      </div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          value="gradient"
                          onChange={(e) =>
                            cornerDotColors.setRadioColor(e.target.value)
                          }
                          checked={cornerDotColors.radioColor === "gradient"}
                          type="radio"
                          name="radioCDColor"
                          id="radioCDGradientColor"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="radioCDGradientColor"
                        >
                          Gradient
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Type Solid color */}
                  {cornerDotColors.radioColor === "single" && (
                    <SolidColorBox
                      elementName={"CornersDotOptions"}
                      solidColor={cornerDotColors.solidColor}
                      onChangeSolidColor={(e) =>
                        cornerDotColors.setSolidColor(e.target.value)
                      }
                    />
                  )}

                  {/* Type Gradient color */}
                  {cornerDotColors.radioColor === "gradient" && (
                    <GradientColorBox
                      elementName={"CornersDotOptions"}
                      gradientColorType={cornerDotColors.gradientColorType}
                      onChangeGradientColorType={(e) =>
                        cornerDotColors.setGradientColorType(e.target.value)
                      }
                      gradientColorStart={cornerDotColors.gradientColorStart}
                      onChangeGradientColorStart={(e) =>
                        cornerDotColors.setGradientColorStart(e.target.value)
                      }
                      gradientColorEnd={cornerDotColors.gradientColorEnd}
                      onChangeGradientColorEnd={(e) =>
                        cornerDotColors.setGradientColorEnd(e.target.value)
                      }
                      gradientColorRotation={
                        cornerDotColors.gradientColorRotation
                      }
                      onChangeGradientColorRotation={(e) =>
                        cornerDotColors.setGradientColorRotation(parseInt(e.target.value) || 0)
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            {/* accordion-item */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="panelOpenHFive">
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelOCFive"
                  aria-expanded="false"
                  aria-controls="panelOCFive"
                >
                  Background Options
                </button>
              </h2>
              <div
                id="panelOCFive"
                className="accordion-collapse collapse"
                aria-labelledby="panelOpenHFive"
              >
                <div className="accordion-body">
                  <div className="row g-3 mb-4 align-items-center">
                    <div className="col-4 col-md-2 text-end">
                      <label
                        htmlFor="inputBGColorType"
                        className="col-form-label"
                      >
                        Color Type
                      </label>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          value="single"
                          onChange={(e) =>
                            backgroundColors.setRadioColor(e.target.value)
                          }
                          checked={backgroundColors.radioColor === "single"}
                          type="radio"
                          name="radioBGColor"
                          id="radioBGSolidColor"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="radioBGSolidColor"
                        >
                          Single
                        </label>
                      </div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          value="gradient"
                          onChange={(e) =>
                            backgroundColors.setRadioColor(e.target.value)
                          }
                          checked={backgroundColors.radioColor === "gradient"}
                          type="radio"
                          name="radioBGColor"
                          id="radioBGGradientColor"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="radioBGGradientColor"
                        >
                          Gradient
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Type Solid color */}
                  {backgroundColors.radioColor === "single" && (
                    <SolidColorBox
                      elementName={"BackgroundOptions"}
                      solidColor={backgroundColors.solidColor}
                      onChangeSolidColor={(e) =>
                        backgroundColors.setSolidColor(e.target.value)
                      }
                    />
                  )}

                  {/* Type Gradient color */}
                  {backgroundColors.radioColor === "gradient" && (
                    <GradientColorBox
                      elementName={"BackgroundOptions"}
                      gradientColorType={backgroundColors.gradientColorType}
                      onChangeGradientColorType={(e) =>
                        backgroundColors.setGradientColorType(e.target.value)
                      }
                      gradientColorStart={backgroundColors.gradientColorStart}
                      onChangeGradientColorStart={(e) =>
                        backgroundColors.setGradientColorStart(e.target.value)
                      }
                      gradientColorEnd={backgroundColors.gradientColorEnd}
                      onChangeGradientColorEnd={(e) =>
                        backgroundColors.setGradientColorEnd(e.target.value)
                      }
                      gradientColorRotation={
                        backgroundColors.gradientColorRotation
                      }
                      onChangeGradientColorRotation={(e) =>
                        backgroundColors.setGradientColorRotation(
                          parseInt(e.target.value) || 0
                        )
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            {/* accordion-item */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="panelOpenHSix">
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelOCSix"
                  aria-expanded="false"
                  aria-controls="panelOCSix"
                >
                  Image Options
                </button>
              </h2>
              <div
                id="panelOCSix"
                className="accordion-collapse collapse"
                aria-labelledby="panelOpenHSix"
              >
                <div className="accordion-body">
                  <div className="d-none row g-3 align-items-center">
                    <div className="col-8 col-md-4 text-end">
                      <label
                        htmlFor="inputHideBGDot"
                        className="col-form-label"
                      >
                        Hide Background Dots
                      </label>
                    </div>
                    <div className="col-4 col-md-2">
                      <input
                        onChange={onChangeHideBGDot}
                        checked={imageOptions.hideBGDot}
                        type="checkbox"
                        id="inputHideBGDot"
                        name="inputHideBGDot"
                        className="form-check-input"
                      />
                    </div>
                  </div>

                  <div className="row g-3 align-items-center">
                    <div className="col-4 col-md-3 text-end">
                      <label
                        htmlFor="inputImageSize"
                        className="col-form-label"
                      >
                        Image size
                      </label>
                    </div>
                    <div className="col-8 col-md-3">
                      <input
                        onChange={onImageSizeChange}
                        value={imageOptions.size}
                        id="inputImageSize"
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        className="form-control"
                      />
                    </div>
                    <div className="col-4 col-md-2 text-end">
                      <label
                        htmlFor="inputImageMargin"
                        className="col-form-label"
                      >
                        Margin (px)
                      </label>
                    </div>
                    <div className="col-8 col-md-3">
                      <input
                        onChange={onImageMarginChange}
                        value={imageOptions.margin}
                        id="inputImageMargin"
                        type="number"
                        min="0"
                        max="500"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* accordion-item */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="panelOpenHSeven">
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelOCSeven"
                  aria-expanded="false"
                  aria-controls="panelOCSeven"
                >
                  QR Options
                </button>
              </h2>
              <div
                id="panelOCSeven"
                className="accordion-collapse collapse"
                aria-labelledby="panelOpenHSeven"
              >
                <div className="accordion-body">
                  <div className="row g-3 mb-4 align-items-center">
                    <div className="col-5 col-md-3 text-end">
                      <label
                        htmlFor="inputQRTypeNumber"
                        className="col-form-label"
                      >
                        Type Number
                      </label>
                    </div>
                    <div className="col-7 col-md-3">
                      <input
                        value={qrOptions.typeNumber}
                        onChange={onQrTypeNumberChange}
                        id="inputQRTypeNumber"
                        type="number"
                        min="2"
                        max="40"
                        className="form-control"
                      />
                    </div>
                    <div className="col-5 col-md-2 text-end">
                      <label htmlFor="inputQRMode" className="col-form-label">
                        Mode
                      </label>
                    </div>
                    <div className="col-7 col-md-3">
                      <select
                        value={qrOptions.mode}
                        onChange={onQrModeChange}
                        id="inputQRMode"
                        className="form-select"
                      >
                        <option value="Numeric">Numeric</option>
                        <option value="Alphanumeric">Alphanumeric</option>
                        <option value="Byte">Byte</option>
                        <option value="Kanji">Kanji</option>
                      </select>
                    </div>
                  </div>

                  <div className="row g-3 align-items-center">
                    <div className="col-8 col-md-4 text-end">
                      <label
                        htmlFor="inputQRCorrectLevel"
                        className="col-form-label"
                      >
                        Error Correction Level
                      </label>
                    </div>
                    <div className="col-4 col-md-2">
                      <select
                        value={qrOptions.correctLevel}
                        onChange={onQrCorrectLvChange}
                        id="inputQRCorrectLevel"
                        className="form-select"
                      >
                        <option value="L">L</option>
                        <option value="M">M</option>
                        <option value="Q">Q</option>
                        <option value="H">H</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PREVIEW and ACTIONS */}
        <div className="col-12 col-md-4">
          <div className="qr-preview">
            <div style={{ position: "relative" }}>
              {(!qrSettings.dataUrl || !String(qrSettings.dataUrl).trim()) && !qrError && (
                <div style={{ width: "100%", maxWidth: "300px", margin: "0 auto", display: "flex", justifyContent: "center" }}>
                  <NoDataPlaceholder 
                    onClick={() => { const el = document.getElementById("inputData") as HTMLInputElement; if (el) el.focus(); }} 
                    onMouseEnter={() => { const el = document.getElementById("inputData") as HTMLInputElement; if (el) el.placeholder = "Enter data here!"; }}
                  />
                </div>
              )}
              {qrError && (
                <div style={{ width: "100%", maxWidth: "300px", margin: "0 auto", display: "flex", justifyContent: "center" }}>
                  <ErrorPlaceholder 
                    message={qrError} 
                    onClick={() => { const el = document.getElementById("inputData") as HTMLInputElement; if (el) el.focus(); }} 
                    onMouseEnter={() => { const el = document.getElementById("inputData") as HTMLInputElement; if (el) el.placeholder = "Enter data here!"; }}
                  />
                </div>
              )}
              <div
                className="content"
                ref={qrRef}
                style={{
                  display: (!qrSettings.dataUrl || !String(qrSettings.dataUrl).trim() || qrError) ? "none" : "block",
                }}
              ></div>
            </div>
            <div className="input-group py-4 justify-content-center">
              <label htmlFor="selectType" className="input-group-text">
                <strong>Type:</strong>
              </label>
              <select
                id="selectType"
                className="form-select"
                onChange={onExtensionChange}
                value={qrSettings.fileExt}
              >
                <option value="png">PNG</option>
                <option value="jpeg">JPEG</option>
                <option value="svg">SVG</option>
              </select>
              <button
                onClick={onDownloadQr}
                type="button"
                className={`${qrSettings.disableDownload ? "disabled" : ""
                  } btn btn-lg btn-success`}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SingleQR;
